/**
 * JourneyRoadmap — visual ladder from current tier to the summit.
 *
 * Drop-in for both the Scribe Journey ("Legendary Archon") and the
 * Reader Journey ("The Voracious"). Pass:
 *   • kind      'writer' | 'reader'
 *   • endpoint  '/api/writer/roadmap' | '/api/reader/roadmap'
 *
 * Each tier card shows:
 *   • Tier title + level band
 *   • Per-metric progress bars (X / Y · pct)
 *   • Overall completion %
 *   • Achievement ✓ when fully met
 *
 * Locked tiers are dimmed; the *next* tier gets an accent ring.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Lock, ChevronRight, Trophy, Crown, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Friendly metric labels — keyed by the metric name returned from the
// backend (writer + reader share field names where they overlap).
const METRIC_LABELS = {
  // writer
  words_published: 'Words published',
  audio_minutes: 'Audio minutes uploaded',
  series_published: 'Series published',
  followers: 'Followers',
  // reader
  books_finished: 'Books finished',
  listen_hours: 'Listen hours',
  streak_days: 'Best reading streak (days)',
  quizzes_passed: 'Quizzes passed',
};

const fmt = (n) => {
  const v = Number(n || 0);
  if (Number.isInteger(v)) return v.toLocaleString();
  return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const TierCard = ({ tier, isNext }) => {
  const achieved = tier.achieved;
  const metrics = Object.entries(tier.progress || {});
  return (
    <div
      className={`relative rounded-xl border p-4 transition ${
        achieved
          ? 'border-emerald-500/60 bg-gradient-to-br from-emerald-950/40 to-slate-900/60'
          : isNext
          ? 'border-fuchsia-500/60 bg-gradient-to-br from-fuchsia-950/30 to-slate-900/60 shadow-[0_0_24px_-8px_rgba(217,70,239,0.5)]'
          : 'border-slate-700/60 bg-slate-900/40 opacity-80'
      }`}
      data-testid={`roadmap-tier-${tier.tier_index}`}
      data-achieved={achieved ? 'true' : 'false'}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Tier {tier.tier_index}
            </span>
            <span className="text-[10px] text-slate-500">
              Lv {tier.level_min}–{tier.level_max}
            </span>
            {achieved && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                <CheckCircle2 className="w-3 h-3" /> Achieved
              </span>
            )}
            {isNext && !achieved && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-fuchsia-300">
                <ChevronRight className="w-3 h-3" /> Next
              </span>
            )}
          </div>
          <h3 className="text-lg font-bold text-white truncate">
            {tier.tier_index === 10 ? (
              <span className="inline-flex items-center gap-1.5">
                <Crown className="w-4 h-4 text-amber-300" />
                {tier.title}
              </span>
            ) : (
              tier.title
            )}
          </h3>
          <p className="text-xs text-slate-400 mt-1">{tier.summary}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Progress</p>
          <p
            className={`text-2xl font-black tabular-nums ${
              achieved ? 'text-emerald-300' : isNext ? 'text-fuchsia-300' : 'text-slate-400'
            }`}
          >
            {Math.round(tier.overall_pct || 0)}%
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {metrics.map(([key, p]) => (
          <div key={key} data-testid={`roadmap-metric-${key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={p.complete ? 'text-emerald-300 font-semibold' : 'text-slate-300'}>
                {p.complete && <CheckCircle2 className="inline w-3 h-3 mr-1" />}
                {METRIC_LABELS[key] || key}
              </span>
              <span className="text-slate-400 tabular-nums">
                {fmt(p.current)} / {fmt(p.target)}
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  p.complete
                    ? 'bg-gradient-to-r from-emerald-400 to-emerald-300'
                    : isNext
                    ? 'bg-gradient-to-r from-fuchsia-500 to-purple-400'
                    : 'bg-gradient-to-r from-slate-600 to-slate-500'
                }`}
                style={{ width: `${Math.min(100, p.pct || 0)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const JourneyRoadmap = ({ kind = 'writer', endpoint }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = endpoint || (kind === 'reader' ? '/api/reader/roadmap' : '/api/writer/roadmap');
        const r = await authFetch(`${BACKEND_URL}${url}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Failed to load roadmap');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, endpoint]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-8 flex items-center justify-center gap-3 text-slate-400" data-testid="roadmap-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading roadmap…
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="rounded-xl border border-rose-500/40 bg-rose-950/30 p-6 text-rose-200 text-sm" data-testid="roadmap-error">
        Couldn&apos;t load the roadmap: {err || 'unknown error'}
      </div>
    );
  }

  const nextTierIndex = data.next_tier?.tier_index;
  const summit = data.summit_title;

  return (
    <div className="space-y-4" data-testid={`journey-roadmap-${kind}`}>
      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-950/30 via-slate-900 to-slate-950 p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-900/40">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">
              Journey to the Summit
            </p>
            <h2 className="text-xl sm:text-2xl font-black text-white">
              Road to <span className="text-amber-300">{summit}</span>
            </h2>
            <p className="text-sm text-slate-300 mt-1">
              {data.next_tier && !data.next_tier.achieved ? (
                <>
                  Next stop: <span className="font-bold text-white">{data.next_tier.title}</span>
                  {' · '}
                  <span className="text-amber-300 font-bold">
                    {Math.round(data.next_tier.overall_pct || 0)}%
                  </span>
                  {' complete'}
                </>
              ) : (
                <span className="text-emerald-300 font-bold">Every tier achieved. You&apos;re the standard.</span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {(data.tiers || []).map((t) => (
          <TierCard key={t.tier_index} tier={t} isNext={t.tier_index === nextTierIndex} />
        ))}
      </div>
    </div>
  );
};

export default JourneyRoadmap;
